import { HugeiconsIcon } from "@hugeicons/react";
import { Add01Icon, Alert01Icon, CheckmarkCircle02Icon, Clock03Icon, Shield01Icon } from "@hugeicons/core-free-icons";
import { useEffect, useState } from 'react';
import { api, apiError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { DemoDataBadge } from '@/components/shared/DemoDataBadge';
import { MotionSection, StaggerContainer, StaggerItem } from '@/components/shared/MotionSection';
import {
  OperationalPageHeader,
  OperationalStatCard,
  operationalCardClass,
} from '@/components/shared/OperationalUI';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const EMPTY_FORM = {
  title: '',
  description: '',
  severity: 'Media',
  owner: '',
  initiative_id: '',
  source: 'Reporte manual',
  patient_impact: false,
  personal_data: false,
};

const severityVariant = {
  Baja: 'outline',
  Media: 'secondary',
  Alta: 'destructive',
  Crítica: 'destructive',
};

export default function Incidents() {
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [initiatives, setInitiatives] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = async () => {
    try {
      const [incidentsResponse, initiativesResponse] = await Promise.all([
        api.get('/incidents'),
        api.get('/initiatives?sort=name'),
      ]);
      setData(incidentsResponse.data);
      setInitiatives(initiativesResponse.data);
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async () => {
    if (!form.title.trim() || !form.description.trim() || !form.owner.trim()) {
      toast.error('Completa título, descripción y responsable');
      return;
    }
    setSaving(true);
    try {
      await api.post('/incidents', {
        ...form,
        initiative_id: form.initiative_id === 'none' ? null : form.initiative_id || null,
      });
      toast.success('Incidente registrado con su SLA de respuesta');
      setForm(EMPTY_FORM);
      setOpen(false);
      await load();
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setSaving(false);
    }
  };

  if (!data) {
    return (
      <div className="mx-auto grid max-w-7xl gap-4 px-6 py-10 md:grid-cols-2">
        {[...Array(6)].map((_, index) => (
          <Skeleton key={index} className="h-48 rounded-2xl" />
        ))}
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-8 px-6 py-10">
      <OperationalPageHeader
        title="Incidentes y respuesta de IA"
        subtitle="Intake único, SLA proporcional, contención, causa raíz, acciones correctivas y evidencia verificable."
        actions={
        <div className="flex flex-wrap items-center gap-2">
          <DemoDataBadge />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#307FE2] text-white hover:bg-[#307FE2]/90">
                <HugeiconsIcon icon={Add01Icon} data-icon="inline-start" />
                Reportar incidente
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-2xl">
              <DialogHeader>
                <DialogTitle>Reportar incidente relacionado con IA</DialogTitle>
                <DialogDescription>
                  No incluyas datos personales ni clínicos en la descripción. Si existe riesgo inmediato para personas,
                  activa además el canal clínico o de seguridad correspondiente.
                </DialogDescription>
              </DialogHeader>
              <div className="grid max-h-[65vh] gap-4 overflow-y-auto py-2 sm:grid-cols-2">
                <div className="flex flex-col gap-2 sm:col-span-2">
                  <Label htmlFor="incident-title">Título</Label>
                  <Input
                    id="incident-title"
                    value={form.title}
                    onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
                    placeholder="Describe brevemente qué ocurrió"
                  />
                </div>
                <div className="flex flex-col gap-2 sm:col-span-2">
                  <Label htmlFor="incident-description">Descripción sin datos sensibles</Label>
                  <Textarea
                    id="incident-description"
                    rows={4}
                    value={form.description}
                    onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
                    placeholder="Qué se observó, cuándo y cuál fue el impacto potencial"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label>Severidad inicial</Label>
                  <Select
                    value={form.severity}
                    onValueChange={(value) => setForm((current) => ({ ...current, severity: value }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {['Baja', 'Media', 'Alta', 'Crítica'].map((severity) => (
                        <SelectItem key={severity} value={severity}>
                          {severity}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="incident-owner">Responsable de respuesta</Label>
                  <Input
                    id="incident-owner"
                    value={form.owner}
                    onChange={(event) => setForm((current) => ({ ...current, owner: event.target.value }))}
                    placeholder="Área o nombre"
                  />
                </div>
                <div className="flex flex-col gap-2 sm:col-span-2">
                  <Label>Iniciativa relacionada</Label>
                  <Select
                    value={form.initiative_id || 'none'}
                    onValueChange={(value) => setForm((current) => ({ ...current, initiative_id: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin iniciativa identificada</SelectItem>
                      {initiatives.map((initiative) => (
                        <SelectItem key={initiative.id} value={initiative.id}>
                          {initiative.code} · {initiative.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Flag
                  id="patient-impact"
                  checked={form.patient_impact}
                  onCheckedChange={(value) => setForm((current) => ({ ...current, patient_impact: Boolean(value) }))}
                  label="Posible impacto en pacientes"
                />
                <Flag
                  id="personal-data"
                  checked={form.personal_data}
                  onCheckedChange={(value) => setForm((current) => ({ ...current, personal_data: Boolean(value) }))}
                  label="Posible exposición de datos"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>
                  Cancelar
                </Button>
                <Button onClick={submit} disabled={saving}>
                  {saving ? 'Registrando…' : 'Registrar y activar SLA'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        }
      />

      <MotionSection delay={0.04}>
        <Alert className="rounded-2xl border-primary/15 bg-primary/[0.03]">
          <HugeiconsIcon icon={Shield01Icon} className="size-4" aria-hidden="true" />
          <AlertTitle>Ruta institucional de respuesta</AlertTitle>
          <AlertDescription>
            Severidad crítica: respuesta en 2 horas; alta: 8 horas; media: 24 horas; baja: 72 horas. El cierre exige causa
            raíz, acción correctiva y evidencia.
          </AlertDescription>
        </Alert>
      </MotionSection>

      <StaggerContainer className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" stagger={0.04}>
        <StaggerItem>
          <OperationalStatCard
            icon={Alert01Icon}
            label="Abiertos"
            value={data.summary.open}
            subtitle="Requieren seguimiento"
            tone="alert"
          />
        </StaggerItem>
        <StaggerItem>
          <OperationalStatCard
            icon={Shield01Icon}
            label="Críticos abiertos"
            value={data.summary.critical}
            subtitle="Respuesta en 2 horas"
            tone="danger"
          />
        </StaggerItem>
        <StaggerItem>
          <OperationalStatCard
            icon={Clock03Icon}
            label="SLA en riesgo"
            value={data.summary.sla_at_risk}
            subtitle="Vencen en menos de 8 horas"
            tone="blue"
          />
        </StaggerItem>
        <StaggerItem>
          <OperationalStatCard
            icon={CheckmarkCircle02Icon}
            label="Contenidos"
            value={data.summary.contained}
            subtitle="Impacto limitado"
            tone="success"
          />
        </StaggerItem>
      </StaggerContainer>

      <StaggerContainer className="grid gap-4 lg:grid-cols-2" stagger={0.04}>
        {data.incidents.map((incident) => (
          <StaggerItem key={incident.id}>
          <Card className={operationalCardClass}>
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <CardTitle>{incident.title}</CardTitle>
                  <CardDescription>{incident.source}</CardDescription>
                </div>
                <Badge variant={severityVariant[incident.severity] || 'outline'}>{incident.severity}</Badge>
              </div>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <p className="text-sm leading-relaxed text-muted-foreground">{incident.description}</p>
              <div className="grid gap-3 rounded-xl bg-muted/50 p-4 text-xs sm:grid-cols-2">
                <Info label="Estado" value={incident.status} />
                <Info label="Responsable" value={incident.owner} />
                <Info label="Vence" value={new Date(incident.due_at).toLocaleString('es-CL')} />
                <Info
                  label="Señales"
                  value={[
                    incident.patient_impact && 'Impacto paciente',
                    incident.personal_data && 'Datos personales',
                  ]
                    .filter(Boolean)
                    .join(' · ') || 'Sin señales críticas'}
                />
              </div>
              {incident.root_cause && (
                <div className="rounded-xl border border-border p-3">
                  <p className="text-xs font-semibold">Causa raíz</p>
                  <p className="mt-1 text-xs text-muted-foreground">{incident.root_cause}</p>
                </div>
              )}
              {can.edit && <IncidentManagement incident={incident} onSaved={load} />}
            </CardContent>
          </Card>
          </StaggerItem>
        ))}
      </StaggerContainer>
    </div>
  );
}

function Flag({ id, checked, onCheckedChange, label }) {
  return (
    <div className="flex items-center gap-2 rounded-xl border border-border p-3">
      <Checkbox id={id} checked={checked} onCheckedChange={onCheckedChange} />
      <Label htmlFor={id} className="text-xs leading-snug">
        {label}
      </Label>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-muted-foreground">{label}</p>
      <p className="mt-0.5 font-medium text-foreground">{value}</p>
    </div>
  );
}

function IncidentManagement({ incident, onSaved }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(incident.status);
  const [rootCause, setRootCause] = useState(incident.root_cause || '');
  const [actions, setActions] = useState((incident.corrective_actions || []).join('\n'));
  const [evidence, setEvidence] = useState((incident.evidence || []).join('\n'));

  const save = async () => {
    if (['Cerrado', 'Resuelto'].includes(status) && (!rootCause.trim() || !actions.trim() || !evidence.trim())) {
      toast.error('El cierre exige causa raíz, acciones correctivas y evidencia');
      return;
    }
    setSaving(true);
    try {
      await api.put(`/incidents/${incident.id}`, {
        status,
        root_cause: rootCause.trim() || null,
        corrective_actions: actions
          .split('\n')
          .map((item) => item.trim())
          .filter(Boolean),
        evidence: evidence
          .split('\n')
          .map((item) => item.trim())
          .filter(Boolean),
      });
      toast.success('Incidente actualizado y auditado');
      setOpen(false);
      await onSaved();
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full">
          Gestionar respuesta y evidencia
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Gestionar incidente</DialogTitle>
          <DialogDescription>{incident.title}</DialogDescription>
        </DialogHeader>
        <div className="grid max-h-[65vh] gap-4 overflow-y-auto py-2">
          <div className="flex flex-col gap-2">
            <Label>Estado</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {['Abierto', 'En investigación', 'Contenido', 'Resuelto', 'Cerrado'].map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor={`root-${incident.id}`}>Causa raíz</Label>
            <Textarea id={`root-${incident.id}`} rows={3} value={rootCause} onChange={(event) => setRootCause(event.target.value)} />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor={`actions-${incident.id}`}>Acciones correctivas · una por línea</Label>
            <Textarea id={`actions-${incident.id}`} rows={4} value={actions} onChange={(event) => setActions(event.target.value)} />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor={`evidence-${incident.id}`}>Evidencia · una referencia por línea</Label>
            <Textarea id={`evidence-${incident.id}`} rows={3} value={evidence} onChange={(event) => setEvidence(event.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancelar
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving ? 'Guardando…' : 'Guardar seguimiento'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
